import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export function useCustomerActivity(customerId: string | null) {
  return useQuery({
    queryKey: ['customer-activity', customerId],
    enabled: !!customerId,
    queryFn: async () => {
      if (!customerId) return [];

      // Step 1: Get lead IDs directly linked to customer
      const { data: directLeads } = await supabase
        .from('leads')
        .select('id')
        .eq('customer_id', customerId);

      // Step 2: Get lead IDs linked via jobs (for orphaned leads that were converted before the fix)
      const { data: jobLinkedLeads } = await supabase
        .from('jobs')
        .select('lead_id')
        .eq('customer_id', customerId)
        .not('lead_id', 'is', null);

      // Step 3: Combine and dedupe lead IDs
      const leadIdSet = new Set<string>([
        ...(directLeads?.map(l => l.id) || []),
        ...(jobLinkedLeads?.map(j => j.lead_id).filter(Boolean) as string[] || [])
      ]);
      const leadIds = Array.from(leadIdSet);

      // Get job IDs for this customer
      const { data: customerJobs } = await supabase
        .from('jobs')
        .select('id')
        .eq('customer_id', customerId);
      
      const jobIds = customerJobs?.map(j => j.id) || [];

      // Fetch all activities in parallel
      const [
        leads,
        leadActivities,
        communications,
        payments,
        jobs,
        jobParts
      ] = await Promise.all([
        // Fetch lead details for lead creation events
        leadIds.length > 0
          ? supabase
              .from('leads')
              .select('id, lead_number, created_at, lead_source_id, customer_phone, lead_sources(name)')
              .in('id', leadIds)
          : { data: [] },
        
        // Lead activities
        leadIds.length > 0
          ? supabase
              .from('lead_activities')
              .select('*, user:profiles!lead_activities_user_id_fkey(full_name)')
              .in('lead_id', leadIds)
              .order('created_at', { ascending: false })
          : { data: [] },
        
        // Communications
        supabase
          .from('customer_communications')
          .select('*, user:profiles!customer_communications_created_by_fkey(full_name)')
          .eq('customer_id', customerId)
          .order('created_at', { ascending: false }),
        
        // Payments
        jobIds.length > 0
          ? supabase
              .from('job_payments')
              .select('*, job:jobs(job_number), user:profiles!job_payments_recorded_by_fkey(full_name)')
              .in('job_id', jobIds)
              .order('payment_date', { ascending: false })
          : { data: [] },
        
        // Jobs
        supabase
          .from('jobs')
          .select('*')
          .eq('customer_id', customerId)
          .order('created_at', { ascending: false }),
        
        // Job parts
        jobIds.length > 0
          ? supabase
              .from('job_parts')
              .select('*, job:jobs(job_number)')
              .in('job_id', jobIds)
              .order('created_at', { ascending: false })
          : { data: [] }
      ]);

      // Combine all activities into a single timeline
      const activities: any[] = [];

      // Add lead creation events (new!)
      if (leads.data) {
        leads.data.forEach((lead: any) => {
          activities.push({
            id: `lead-created-${lead.id}`,
            type: 'lead_created',
            timestamp: lead.created_at,
            description: `Lead ${lead.lead_number} created`,
            metadata: {
              source: lead.lead_sources?.name,
              phone: lead.customer_phone,
              lead_id: lead.id
            }
          });
        });
      }

      // Add lead activities
      if (leadActivities.data) {
        leadActivities.data.forEach((activity: any) => {
          activities.push({
            id: `lead-${activity.id}`,
            type: 'lead_activity',
            timestamp: activity.created_at,
            description: activity.description,
            user: activity.user,
            metadata: activity.metadata
          });
        });
      }

      // Add communications
      if (communications.data) {
        communications.data.forEach((comm: any) => {
          activities.push({
            id: `comm-${comm.id}`,
            type: 'communication',
            timestamp: comm.created_at,
            description: `${comm.communication_type.toUpperCase()}: ${comm.subject || comm.content?.substring(0, 50) || 'Communication logged'}`,
            user: comm.user,
            metadata: {
              communication_type: comm.communication_type,
              direction: comm.direction,
              outcome: comm.outcome
            }
          });
        });
      }

      // Add payments
      if (payments.data) {
        payments.data.forEach((payment: any) => {
          activities.push({
            id: `payment-${payment.id}`,
            type: 'payment',
            timestamp: payment.payment_date,
            description: `Payment received for ${payment.job?.job_number || 'job'}`,
            user: payment.user,
            metadata: {
              amount: payment.amount,
              payment_method: payment.payment_method
            }
          });
        });
      }

      // Add job creation events
      if (jobs.data) {
        jobs.data.forEach((job: any) => {
          activities.push({
            id: `job-created-${job.id}`,
            type: 'job_created',
            timestamp: job.created_at,
            description: `Job ${job.job_number} created`,
            metadata: {
              status: job.status,
              quoted_price: job.quoted_price
            }
          });

          // Add job status changes (completed/cancelled)
          if (job.closed_at) {
            activities.push({
              id: `job-completed-${job.id}`,
              type: 'job_status',
              timestamp: job.closed_at,
              description: `Job ${job.job_number} completed`,
              metadata: {
                status: 'completed',
                revenue: job.invoice_total
              }
            });
          }
          if (job.cancelled_at) {
            activities.push({
              id: `job-cancelled-${job.id}`,
              type: 'job_status',
              timestamp: job.cancelled_at,
              description: `Job ${job.job_number} cancelled`,
              metadata: {
                status: 'cancelled',
                reason: job.cancellation_reason
              }
            });
          }
        });
      }

      // Add parts
      if (jobParts.data) {
        jobParts.data.forEach((part: any) => {
          activities.push({
            id: `part-${part.id}`,
            type: 'part_added',
            timestamp: part.created_at,
            description: `Part added to ${part.job?.job_number || 'job'}: ${part.part_name}`,
            metadata: {
              quantity: part.quantity,
              cost: part.part_cost
            }
          });
        });
      }

      // Sort all activities by timestamp (newest first)
      return activities.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
    }
  });
}
